import { createHash, randomBytes } from "node:crypto";
import http from "node:http";
import type { AddressInfo } from "node:net";
import type { AgentStore } from "@mewbleh/nori-persistence";
import { readResponseJson, readResponseText } from "./http.js";

export const OPENAI_OAUTH_CLIENT_ID = "app_EMoamEEZ73f0CkXaXp7hrann";
export const OPENAI_OAUTH_ISSUER = "https://auth.openai.com";
const OPENAI_OAUTH_PROFILE = "oauth";
const MAX_AUTH_RESPONSE_BYTES = 1024 * 1024;

export type OpenAiOAuthCredential = {
  version: 1;
  type: "oauth";
  provider: "openai";
  accessToken: string;
  refreshToken: string;
  idToken: string;
  accountId: string;
  expiresAt: number;
  createdAt: number;
  updatedAt: number;
};

export type OpenAiDeviceCode = {
  verificationUrl: string;
  userCode: string;
  expiresAt: number;
  intervalSeconds: number;
  deviceAuthId: string;
};

type OpenAiTokenResponse = { id_token?: string; access_token?: string; refresh_token?: string; expires_in?: number };

function normalizeIssuer(issuer = OPENAI_OAUTH_ISSUER): string {
  const url = new URL(issuer);
  const loopback = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "::1";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && loopback)) throw new Error("OpenAI OAuth issuer must use HTTPS");
  url.username = "";
  url.password = "";
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/u, "");
}

function signalFor(signal: AbortSignal | undefined, timeoutMs: number): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

async function readJson<T>(response: Response): Promise<T> {
  return readResponseJson<T>(response, MAX_AUTH_RESPONSE_BYTES, "OpenAI OAuth response");
}

async function authError(response: Response, operation: string): Promise<Error> {
  const text = (await readResponseText(response, 64 * 1024, "OpenAI OAuth error response").catch(() => "")).slice(0, 1000);
  let message = text || response.statusText;
  try {
    const parsed = JSON.parse(text) as { error?: string | { message?: string; code?: string }; error_description?: string };
    message = typeof parsed.error === "string" ? parsed.error_description ?? parsed.error : parsed.error?.message ?? parsed.error?.code ?? message;
  } catch {}
  return Object.assign(new Error(`${operation} failed (${response.status}): ${message}`), { status: response.status });
}

export function decodeJwtPayload(token: string): Record<string, unknown> | undefined {
  const parts = token.split(".");
  if (parts.length !== 3 || !parts[1]) return undefined;
  try {
    const value = JSON.parse(Buffer.from(parts[1], "base64url").toString("utf8")) as unknown;
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
  } catch {
    return undefined;
  }
}

function jwtExpiry(token: string, now = Date.now()): number {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : now + 5 * 60_000;
}

function jwtAccountId(...tokens: string[]): string | undefined {
  for (const token of tokens) {
    const auth = decodeJwtPayload(token)?.["https://api.openai.com/auth"];
    if (!auth || typeof auth !== "object" || Array.isArray(auth)) continue;
    const accountId = (auth as Record<string, unknown>).chatgpt_account_id;
    if (typeof accountId === "string" && accountId) return accountId;
  }
  return undefined;
}

function credentialFromTokens(tokens: OpenAiTokenResponse, previous?: OpenAiOAuthCredential): OpenAiOAuthCredential {
  const accessToken = tokens.access_token ?? previous?.accessToken;
  const refreshToken = tokens.refresh_token ?? previous?.refreshToken;
  const idToken = tokens.id_token ?? previous?.idToken;
  if (!accessToken || !refreshToken || !idToken) throw new Error("OpenAI OAuth token response is missing required tokens");
  const accountId = jwtAccountId(accessToken, idToken) ?? previous?.accountId;
  if (!accountId) throw new Error("OpenAI OAuth token is missing the ChatGPT account id claim");
  const now = Date.now();
  return {
    version: 1,
    type: "oauth",
    provider: "openai",
    accessToken,
    refreshToken,
    idToken,
    accountId,
    expiresAt: typeof tokens.expires_in === "number" ? now + tokens.expires_in * 1000 : jwtExpiry(accessToken, now),
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
  };
}

export function saveOpenAiOAuthCredential(store: AgentStore, credential: OpenAiOAuthCredential): void {
  store.setCredential("openai", OPENAI_OAUTH_PROFILE, JSON.stringify(credential));
}

export function loadOpenAiOAuthCredential(store: AgentStore): OpenAiOAuthCredential | undefined {
  const stored = store.getCredential("openai", OPENAI_OAUTH_PROFILE);
  if (!stored) return undefined;
  try {
    const credential = JSON.parse(stored) as OpenAiOAuthCredential;
    if (credential.version !== 1 || credential.type !== "oauth" || credential.provider !== "openai" || !credential.accessToken || !credential.refreshToken || !credential.accountId) return undefined;
    return credential;
  } catch {
    return undefined;
  }
}

export function deleteOpenAiOAuthCredential(store: AgentStore): boolean {
  return store.deleteCredential("openai", OPENAI_OAUTH_PROFILE);
}

export function openAiOAuthStatus(store: AgentStore) {
  const credential = loadOpenAiOAuthCredential(store);
  if (!credential) return { provider: "openai", method: "oauth", authenticated: false };
  const claims = decodeJwtPayload(credential.idToken);
  const auth = claims?.["https://api.openai.com/auth"] as Record<string, unknown> | undefined;
  const profile = claims?.["https://api.openai.com/profile"] as Record<string, unknown> | undefined;
  return {
    provider: "openai",
    method: "oauth",
    authenticated: true,
    accountId: credential.accountId,
    expiresAt: credential.expiresAt,
    expired: credential.expiresAt <= Date.now(),
    ...(typeof claims?.email === "string" ? { email: claims.email } : typeof profile?.email === "string" ? { email: profile.email } : {}),
    ...(typeof auth?.chatgpt_plan_type === "string" ? { plan: auth.chatgpt_plan_type } : {}),
  };
}

export async function requestOpenAiDeviceCode(options: { fetch?: typeof fetch; issuer?: string; clientId?: string; signal?: AbortSignal } = {}): Promise<OpenAiDeviceCode> {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const issuer = normalizeIssuer(options.issuer);
  const response = await fetchImpl(`${issuer}/api/accounts/deviceauth/usercode`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ client_id: options.clientId ?? OPENAI_OAUTH_CLIENT_ID }),
    signal: signalFor(options.signal, 30_000),
  });
  if (!response.ok) throw await authError(response, "OpenAI device-code request");
  const body = await readJson<{ device_auth_id?: string; user_code?: string; usercode?: string; interval?: string | number }>(response);
  const userCode = body.user_code ?? body.usercode;
  const intervalSeconds = Number(body.interval ?? 5);
  if (!body.device_auth_id || !userCode || !Number.isFinite(intervalSeconds) || intervalSeconds < 0) throw new Error("OpenAI device-code response is invalid");
  return {
    verificationUrl: `${issuer}/codex/device`,
    userCode,
    expiresAt: Date.now() + 15 * 60_000,
    intervalSeconds,
    deviceAuthId: body.device_auth_id,
  };
}

async function exchangeAuthorizationCode(options: { authorizationCode: string; codeVerifier: string; redirectUri: string; fetch?: typeof fetch; issuer?: string; clientId?: string; signal?: AbortSignal }): Promise<OpenAiOAuthCredential> {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const issuer = normalizeIssuer(options.issuer);
  const response = await fetchImpl(`${issuer}/oauth/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code: options.authorizationCode,
      redirect_uri: options.redirectUri,
      client_id: options.clientId ?? OPENAI_OAUTH_CLIENT_ID,
      code_verifier: options.codeVerifier,
    }),
    signal: signalFor(options.signal, 30_000),
  });
  if (!response.ok) throw await authError(response, "OpenAI OAuth token exchange");
  return credentialFromTokens(await readJson<OpenAiTokenResponse>(response));
}

export async function completeOpenAiDeviceCode(deviceCode: OpenAiDeviceCode, options: { fetch?: typeof fetch; issuer?: string; clientId?: string; signal?: AbortSignal } = {}): Promise<OpenAiOAuthCredential> {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const issuer = normalizeIssuer(options.issuer);
  while (Date.now() < deviceCode.expiresAt) {
    if (options.signal?.aborted) throw new DOMException("OpenAI device login cancelled", "AbortError");
    const response = await fetchImpl(`${issuer}/api/accounts/deviceauth/token`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ device_auth_id: deviceCode.deviceAuthId, user_code: deviceCode.userCode }),
      signal: signalFor(options.signal, 30_000),
    });
    if (response.ok) {
      const body = await readJson<{ authorization_code?: string; code_challenge?: string; code_verifier?: string }>(response);
      if (!body.authorization_code || !body.code_verifier || !body.code_challenge) throw new Error("OpenAI device authorization response is invalid");
      const challenge = createHash("sha256").update(body.code_verifier).digest("base64url");
      if (challenge !== body.code_challenge) throw new Error("OpenAI device authorization PKCE challenge mismatch");
      return exchangeAuthorizationCode({
        authorizationCode: body.authorization_code,
        codeVerifier: body.code_verifier,
        redirectUri: `${issuer}/deviceauth/callback`,
        fetch: fetchImpl,
        issuer,
        ...(options.clientId ? { clientId: options.clientId } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    }
    if (response.status !== 403 && response.status !== 404) throw await authError(response, "OpenAI device authorization");
    await response.body?.cancel().catch(() => undefined);
    const delay = Math.min(Math.max(deviceCode.intervalSeconds, 0), Math.max(0, (deviceCode.expiresAt - Date.now()) / 1000));
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => options.signal?.removeEventListener("abort", abort);
      const timer = setTimeout(() => { cleanup(); resolve(); }, delay * 1000);
      const abort = () => { clearTimeout(timer); cleanup(); reject(new DOMException("OpenAI device login cancelled", "AbortError")); };
      options.signal?.addEventListener("abort", abort, { once: true });
    });
  }
  throw new Error("OpenAI device login timed out after 15 minutes");
}

export async function refreshOpenAiOAuthCredential(credential: OpenAiOAuthCredential, options: { fetch?: typeof fetch; issuer?: string; clientId?: string; signal?: AbortSignal } = {}): Promise<OpenAiOAuthCredential> {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const issuer = normalizeIssuer(options.issuer);
  const response = await fetchImpl(`${issuer}/oauth/token`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ client_id: options.clientId ?? OPENAI_OAUTH_CLIENT_ID, grant_type: "refresh_token", refresh_token: credential.refreshToken }),
    signal: signalFor(options.signal, 30_000),
  });
  if (!response.ok) throw await authError(response, "OpenAI OAuth token refresh");
  return credentialFromTokens(await readJson<OpenAiTokenResponse>(response), credential);
}

export async function resolveOpenAiOAuthCredential(store: AgentStore, options: { fetch?: typeof fetch; issuer?: string; signal?: AbortSignal; refreshBeforeMs?: number } = {}): Promise<OpenAiOAuthCredential | undefined> {
  const credential = loadOpenAiOAuthCredential(store);
  if (!credential) return undefined;
  if (credential.expiresAt > Date.now() + (options.refreshBeforeMs ?? 2 * 60_000)) return credential;
  const refreshed = await refreshOpenAiOAuthCredential(credential, options);
  saveOpenAiOAuthCredential(store, refreshed);
  return refreshed;
}

export async function beginOpenAiBrowserOAuth(options: { fetch?: typeof fetch; issuer?: string; clientId?: string; signal?: AbortSignal } = {}): Promise<{
  authorizationUrl: string;
  redirectUri: string;
  complete: Promise<OpenAiOAuthCredential>;
  cancel(): void;
}> {
  const issuer = normalizeIssuer(options.issuer);
  const verifier = randomBytes(48).toString("base64url");
  const challenge = createHash("sha256").update(verifier).digest("base64url");
  const state = randomBytes(32).toString("base64url");
  let settle!: (value: OpenAiOAuthCredential) => void;
  let reject!: (reason: unknown) => void;
  const complete = new Promise<OpenAiOAuthCredential>((resolve, rejectPromise) => { settle = resolve; reject = rejectPromise; });
  const controller = new AbortController();
  const server = http.createServer((request, response) => {
    void (async () => {
      const url = new URL(request.url ?? "/", "http://localhost");
      if (request.method !== "GET" || url.pathname !== "/auth/callback") {
        response.writeHead(404, { "content-type": "text/plain; charset=utf-8", "x-content-type-options": "nosniff" });
        response.end("Not found\n");
        return;
      }
      if (url.searchParams.get("state") !== state || !url.searchParams.get("code")) {
        response.writeHead(400, { "content-type": "text/plain; charset=utf-8", "x-content-type-options": "nosniff" });
        response.end("Nori OAuth callback validation failed.\n");
        reject(new Error("OpenAI OAuth callback state mismatch or missing code"));
        server.close();
        return;
      }
      try {
        const credential = await exchangeAuthorizationCode({
          authorizationCode: url.searchParams.get("code")!,
          codeVerifier: verifier,
          redirectUri,
          ...(options.fetch ? { fetch: options.fetch } : {}),
          issuer,
          ...(options.clientId ? { clientId: options.clientId } : {}),
          signal: controller.signal,
        });
        response.writeHead(200, { "content-type": "text/plain; charset=utf-8", "x-content-type-options": "nosn", "cache-control": "no-store" });
        response.end("Nori is signed in with OpenAI. You can close this tab.\n");
        settle(credential);
      } catch (error) {
        response.writeHead(502, { "content-type": "text/plain; charset=utf-8", "x-content-type-options": "nosn", "cache-control": "no-store" });
        response.end("Nori could not complete OpenAI sign-in. Return to the terminal.\n");
        reject(error);
      } finally {
        server.close();
      }
    })();
  });
  const listen = (port: number) => new Promise<void>((resolve, rejectListen) => {
    server.once("error", rejectListen);
    server.listen(port, "127.0.0.1", () => { server.off("error", rejectListen); resolve(); });
  });
  try { await listen(1455); } catch { await listen(1457); }
  const address = server.address() as AddressInfo;
  const redirectUri = `http://localhost:${address.port}/auth/callback`;
  const authorization = new URL(`${issuer}/oauth/authorize`);
  for (const [key, value] of Object.entries({
    response_type: "code",
    client_id: options.clientId ?? OPENAI_OAUTH_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: "openid profile email offline_access api.connectors.read api.connectors.invoke",
    code_challenge: challenge,
    code_challenge_method: "S256",
    id_token_add_organizations: "true",
    codex_cli_simplified_flow: "true",
    state,
    originator: "nori",
  })) authorization.searchParams.set(key, value);
  const timeout = setTimeout(() => { reject(new Error("OpenAI browser OAuth timed out")); controller.abort(); server.close(); }, 10 * 60_000);
  timeout.unref();
  complete.finally(() => clearTimeout(timeout)).catch(() => undefined);
  options.signal?.addEventListener("abort", () => { reject(new DOMException("OpenAI login cancelled", "AbortError")); controller.abort(); server.close(); }, { once: true });
  return {
    authorizationUrl: authorization.toString(),
    redirectUri,
    complete,
    cancel: () => { reject(new DOMException("OpenAI login cancelled", "AbortError")); controller.abort(); server.close(); },
  };
}
